/**
 * 「这一次数据变了，要不要重建草稿 / 重建之后谁决定视图」——家长端作业录入表单
 * **草稿同步判定**的纯函数版本（React 那一半留在页面里）。
 *
 * ## 它修的是什么（2026-10-14 用户实测）
 *
 * 用户原话：
 *
 * > 在录入作业界面，点「复制上次结构」后，**在所在面并没有显示复制过来的作业**。
 * > 但点了「退出录入」之后，又显示了这些作业。
 *
 * 复制是**写入**（服务端真落库了，所以"退出录入"以后看得到），问题出在
 * "录入态的草稿没有被重建"这一步。页面里那条重建规则本来是对的：
 *
 *   - **日期变了** → 重建（换一天重新录）；
 *   - **我们自己刚写完**（同步令牌 +1）→ 重建，以服务端结果为准；
 *   - **后台刷新**（别的失效让这一天被重新拉取）→ **不重建** ——
 *     否则家长正在手机上输入的内容会被整段抹掉。
 *
 * 但"我们自己刚写完"这一次重建**必须用写完之后的真实数据**。旧实现里那个数据
 * 是查询缓存里的 `day`，而它那时还是**写之前**的那一份（失效触发的重取是异步的，
 * 见 `homeworkWriteOnSuccess()` 的注释）——于是：
 *
 *   1. 重建出来的是旧草稿（表单看起来"没反应"，正是用户报的那一幕）；
 *   2. 同步键却已经被消费掉，等新数据真的回来时反而被当成"后台刷新"忽略
 *      → **永远**不会再重建。
 *
 * 数据侧由 `homeworkWriteOnSuccess()` 修（写完先把响应播种进缓存）；
 * 这里修的是另一半：**视图不能再由"旧数据里的 `exists`"来推导** ——
 * 否则"在录入态点复制"会被人一脚踢到查看态（用户要的是"在所在面看到"）。
 *
 * ## 两条判定（纯函数，可测）
 *
 *   - `rebuild`：这一份数据要不要用来重建草稿；
 *   - `deriveMode`：视图要不要由 `exists` 推导（false = **保留调用方刚设的视图**：
 *     保存完了看结果、清空后继续录、复制后留在原来的那一屏）。
 *
 * ⚠️ 判定的输入只有一个**key**（`日期#令牌`）加一个**日期**，刻意不做别的推测：
 * "这一天变没变"必须由令牌或日期说话，不能靠比较数据内容 ——
 * 内容比较会把"孩子刚提交了一项"也当成"该抹掉家长正在输入的东西"。
 */

/** 页面里那三个 ref 的值（抽出来就是为了能直接对表测试）。 */
export interface HomeworkDraftSyncInput {
  /** 上一次重建草稿时用的 key（`${date}#${token}`；空串 = 还没重建过）。 */
  syncedKey: string;
  /** 这一次数据的 key。 */
  nextKey: string;
  /** 上一次重建草稿时用的**日期**（空串 = 还没重建过）。 */
  syncedDate: string;
  /** 这一次数据的日期。 */
  dayDate: string;
}

/** 这一次数据变化该怎么处理。 */
export interface HomeworkDraftSyncDecision {
  /** 用这一份数据重建草稿（false = 草稿保持家长正在编辑的样子）。 */
  rebuild: boolean;
  /** 由 `exists` 推导视图（false = 保留调用方刚设的视图）。 */
  deriveMode: boolean;
}

/**
 * 草稿重建的**触发条件与来源判定**。
 *
 * | 情形 | key | 日期 | rebuild | deriveMode |
 * | --- | --- | --- | --- | --- |
 * | 第一次拿到这一天的数据 | 变 | 变（空 → 某天） | ✅ | ✅（这一天有作业就看，没有就直接录） |
 * | 翻到另一天 | 变 | 变 | ✅ | ✅ |
 * | **我们自己刚写完**（保存 / 复制 / 清空） | 变（令牌 +1） | **不变** | ✅ | ❌（保留调用方设的视图） |
 * | 后台刷新（窗口聚焦、别处失效） | 不变 | 不变 | ❌ | ❌（绝不抹掉正在输入的内容） |
 */
export function homeworkDraftSyncDecision(
  input: HomeworkDraftSyncInput,
): HomeworkDraftSyncDecision {
  if (input.syncedKey === input.nextKey) {
    return { rebuild: false, deriveMode: false };
  }
  return { rebuild: true, deriveMode: input.syncedDate !== input.dayDate };
}
